import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { UserPlus, Mail, Shield, User, Clock, Activity, MoreVertical, Trash2, Crown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { teamService, TeamInvite, TeamMember, Team } from "@/services/teamService";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useAuth } from "@/contexts/AuthContext";
import { formatLocalDate, formatLocalDateTime } from "@/lib/date";
import { TeamRole } from "@/types/roles";

interface ActivityItem {
  id: string;
  user: string;
  action: string;
  timestamp: string;
  sortTimestamp: number;
  type: "invite" | "join";
}

interface DisplayMember {
  id: string;
  name: string;
  email: string;
  role: TeamRole;
  status: "active" | "pending" | "suspended";
  joinedDate: string;
  isInvite: boolean;
}

const Team = () => {
  const { canInviteMembers, canManageTeam } = useRoleAccess();
  const { isLoading: authLoading, user } = useAuth();
  const isAdminLike = canManageTeam || user?.teamRole === "admin" || user?.teamRole === "super_admin";

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TeamRole>("viewer");
  const [team, setTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamInvites, setTeamInvites] = useState<TeamInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<DisplayMember | null>(null);

  const isOwner = !!(user?.email && team?.adminEmail && user.email.toLowerCase() === team.adminEmail.toLowerCase());

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [teamData, membersData, invitesData] = await Promise.all([
        teamService.getTeam(),
        teamService.getTeamMembers(),
        teamService.getTeamInvites(),
      ]);

      setTeam(teamData);
      setTeamMembers(membersData);
      setTeamInvites(invitesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load team data";
      setError(errorMessage);
      toast.error("Error loading team data", {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleBadgeVariant = (role: TeamRole) => {
    switch (role) {
      case "super_admin":
        return "default";
      case "admin":
        return "secondary";
      case "viewer":
        return "outline";
      default:
        return "outline";
    }
  };

  const getRoleLabel = (role: TeamRole) => {
    if (role === "super_admin") return "Super Admin";
    return role === "admin" ? "Admin" : "Viewer";
  };

  const getRoleIcon = (role: TeamRole) => {
    switch (role) {
      case "super_admin":
      case "admin": 
        return <Shield className="w-3 h-3" />;
      default:
        return <User className="w-3 h-3" />;
    }
  };

  const getActivityIcon = (type: ActivityItem["type"]) => {
    switch (type) {
      case "invite":
        return <UserPlus className="w-4 h-4 text-blue-500" />;
      case "join":
        return <Activity className="w-4 h-4 text-green-500" />;
    }
  };

  const formatMemberJoinedDate = (joinedAt?: string) => {
    if (!joinedAt) return "Pending";
    return formatLocalDate(joinedAt);
  };

  const toDisplayMembers = useMemo<DisplayMember[]>(() => {
    const members: DisplayMember[] = teamMembers.map((member) => ({
      id: member.id,
      name: member.userName,
      email: member.userEmail,
      role: member.role,
      status: member.status,
      joinedDate: formatMemberJoinedDate(member.joinedAt),
      isInvite: false,
    }));

    const pendingInvites = teamInvites.filter((invite) => invite.status === "pending");
    const invites: DisplayMember[] = pendingInvites.map((invite) => ({
      id: invite.id,
      name: invite.email.split("@")[0],
      email: invite.email,
      role: invite.role,
      status: "pending",
      joinedDate: formatLocalDate(invite.invitedAt),
      isInvite: true,
    }));

    return [...members, ...invites];
  }, [teamMembers, teamInvites]);

  const activityItems = useMemo<ActivityItem[]>(() => {
    const inviteActivity = teamInvites.map((invite) => ({
      id: `invite-${invite.id}`,
      user: invite.email,
      action: `invited as ${getRoleLabel(invite.role)}`,
      timestamp: formatLocalDateTime(invite.invitedAt),
      sortTimestamp: new Date(invite.invitedAt).getTime(),
      type: "invite" as const,
    }));

    const joinActivity = teamMembers
      .filter((member) => member.joinedAt)
      .map((member) => ({
        id: `join-${member.id}`,
        user: member.userName,
        action: "joined the team",
        timestamp: formatLocalDateTime(member.joinedAt as string),
        sortTimestamp: new Date(member.joinedAt as string).getTime(),
        type: "join" as const,
      }));

    return [...inviteActivity, ...joinActivity]
      .sort((a, b) => b.sortTimestamp - a.sortTimestamp)
      .slice(0, 8);
  }, [teamInvites, teamMembers]);

  const isLastAdminLike = (memberId: string) => {
    const adminMembers = teamMembers.filter((member) => member.role === "admin" || member.role === "super_admin");
    const targetMember = teamMembers.find((member) => member.id === memberId);
    return adminMembers.length === 1 && (targetMember?.role === "admin" || targetMember?.role === "super_admin");
  };

  const wouldLeaveNoAdmins = (memberId: string, newRole: TeamRole) => {
    if (newRole === "admin" || newRole === "super_admin") return false;
    const adminMembers = teamMembers.filter((member) => member.role === "admin" || member.role === "super_admin");
    const targetMember = teamMembers.find((member) => member.id === memberId);
    return adminMembers.length === 1 && (targetMember?.role === "admin" || targetMember?.role === "super_admin");
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error("Please enter an email address");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const newMember = await teamService.inviteTeamMember({
        email: inviteEmail,
        role: inviteRole,
      });

      if (newMember.userId !== "00000000-0000-0000-0000-000000000000") {
        setTeamMembers([newMember, ...teamMembers]);
      } else {
        const updatedInvites = await teamService.getTeamInvites();
        setTeamInvites(updatedInvites);
      }

      setInviteEmail("");
      setInviteRole("viewer");

      toast.success("Invitation sent", {
        description: `Invite sent to ${inviteEmail}`,
      });

      const updatedTeam = await teamService.getTeam();
      setTeam(updatedTeam);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to send invitation";
      toast.error("Error sending invitation", {
        description: errorMessage,
      });
    }
  };

  const handleRoleChange = async (memberId: string, newRole: TeamRole) => {
    if (wouldLeaveNoAdmins(memberId, newRole)) {
      toast.error("Cannot change role", {
        description: "At least one admin must remain on the team.",
      });
      return;
    }

    const member = teamMembers.find((m) => m.id === memberId);
    if (!member) return;

    const confirmed = window.confirm(`Change ${member.userName}'s role to ${getRoleLabel(newRole)}?`);
    if (!confirmed) return;

    try {
      const updatedMember = await teamService.updateTeamMember(memberId, { role: newRole });
      setTeamMembers(teamMembers.map((item) => (item.id === memberId ? updatedMember : item)));
      toast.success("Role updated successfully");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update role";
      toast.error("Error updating role", {
        description: errorMessage,
      });
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (isLastAdminLike(memberId)) {
      toast.error("Cannot remove member", {
        description: "At least one admin must remain on the team.",
      });
      return;
    }

    const member = teamMembers.find((m) => m.id === memberId);
    if (!member) return;

    const confirmed = window.confirm(`Remove ${member.userName} from the team? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await teamService.removeTeamMember(memberId);
      setTeamMembers(teamMembers.filter((item) => item.id !== memberId));
      toast.success("Team member removed");

      const updatedTeam = await teamService.getTeam();
      setTeam(updatedTeam);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to remove member";
      toast.error("Error removing member", {
        description: errorMessage,
      });
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await teamService.cancelInvite(inviteId);
      setTeamInvites(teamInvites.filter((invite) => invite.id !== inviteId));
      toast.success("Invitation cancelled");

      const updatedTeam = await teamService.getTeam();
      setTeam(updatedTeam);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to cancel invitation";
      toast.error("Error cancelling invitation", {
        description: errorMessage,
      });
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferTarget) return;

    try {
      await teamService.updateTeam({ adminEmail: transferTarget.email });
      const updatedTeam = await teamService.getTeam();
      setTeam(updatedTeam);
      toast.success("Ownership transferred", {
        description: `${transferTarget.name} is now the team owner.`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to transfer ownership";
      toast.error("Error transferring ownership", {
        description: errorMessage,
      });
    } finally {
      setTransferTarget(null);
    }
  };

  if (authLoading || isLoading || !user) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading team data...</p>
        </div>
      </div>
    );
  }

  if (error && !team) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error: {error}</p>
          <Button onClick={loadTeamData} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header removed */}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invite Member Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Invite Team Member
                </CardTitle>
                <CardDescription>Send an invitation to join your team</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="colleague@recallio.ai"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="pl-10"
                        disabled={!canInviteMembers}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={inviteRole}
                      onValueChange={(value) => setInviteRole(value as TeamRole)}
                      disabled={!canInviteMembers}
                    >
                      <SelectTrigger id="role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Viewer
                          </div>
                        </SelectItem>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            Admin
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleInvite} className="w-full md:w-auto mt-4" disabled={!canInviteMembers}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Send Invitation
                </Button>
              </CardContent>
            </Card>

            {/* Team Members Card */}
            <Card>
              <CardHeader>
                <CardTitle>Team Members ({toDisplayMembers.length})</CardTitle>
                <CardDescription>Manage your team members and their roles</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {toDisplayMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <Avatar className="w-12 h-12">
                          <AvatarImage alt={member.name} />
                          <AvatarFallback>{member.name.split(" ").map(n => n[0]).join("")}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-foreground truncate">{member.name}</p>
                            {team?.adminEmail && member.email.toLowerCase() === team.adminEmail.toLowerCase() && (
                              <Badge variant="default" className="gap-1 bg-amber-500 hover:bg-amber-600 text-white">
                                <Crown className="w-3 h-3" />
                                Owner
                              </Badge>
                            )}
                            <Badge variant={getRoleBadgeVariant(member.role)} className="gap-1">
                              {getRoleIcon(member.role)}
                              {getRoleLabel(member.role)}
                            </Badge>
                            {member.status === "pending" && (
                              <Badge variant="outline" className="text-amber-500 border-amber-500">
                                Pending
                              </Badge>
                            )}
                            {member.status === "suspended" && (
                              <Badge variant="outline" className="text-destructive border-destructive">
                                Suspended
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            Joined {member.joinedDate}
                          </p>
                        </div>
                      </div>
                      
                      {isAdminLike && member.role !== "super_admin" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.isInvite ? (
                              <DropdownMenuItem
                                onClick={() => handleCancelInvite(member.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Cancel Invitation
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleRoleChange(member.id, member.role === "admin" ? "viewer" : "admin")}
                                  disabled={wouldLeaveNoAdmins(member.id, member.role === "admin" ? "viewer" : "admin")}
                                  className={wouldLeaveNoAdmins(member.id, member.role === "admin" ? "viewer" : "admin") ? "opacity-50 cursor-not-allowed" : ""}
                                >
                                  Change Role
                                </DropdownMenuItem>
                                {isOwner && member.role === "admin" && (
                                  <>
                                    <Separator className="my-1" />
                                    <DropdownMenuItem onClick={() => setTransferTarget(member)} className="text-amber-600">
                                      <Crown className="w-4 h-4 mr-2" />
                                      Transfer Ownership
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <Separator className="my-1" />
                                <DropdownMenuItem
                                  onClick={() => handleRemoveMember(member.id)}
                                  className={`text-destructive ${isLastAdminLike(member.id) ? "opacity-50 cursor-not-allowed" : ""}`}
                                  disabled={isLastAdminLike(member.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove Member
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Role Permissions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Role Permissions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">Super Admin</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    Full access to all features, settings, and billing.
                  </p>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-secondary-foreground" />
                    <span className="font-semibold text-sm">Admin</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    Manage members, roles, and most settings. Cannot delete workspace.
                  </p>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="font-semibold text-sm">Viewer</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-6">
                    View-only access to agents, calls, and analytics.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Activity Log */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activityItems.length === 0 && (
                    <p className="text-sm text-muted-foreground">No recent team activity yet.</p>
                  )}
                  {activityItems.map((activity) => (
                    <div key={activity.id} className="flex gap-3">
                      <div className="mt-0.5">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm">
                          <span className="font-semibold">{activity.user}</span>{" "}
                          <span className="text-muted-foreground">{activity.action}</span>
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {activity.timestamp}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <AlertDialog open={!!transferTarget} onOpenChange={(open) => { if (!open) setTransferTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer Team Ownership</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to transfer ownership to <span className="font-semibold text-foreground">{transferTarget?.name}</span> ({transferTarget?.email})?
              You will lose owner privileges and this action cannot be undone without the new owner's consent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleTransferOwnership} className="bg-amber-500 hover:bg-amber-600">
              Transfer Ownership
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Team;
